import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, Inbox, Clock, Users, PanelLeft } from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '@/context/AuthContext';
import { useSidebar } from '@/hooks/useSidebar';
import { SidebarHeader } from './sidebar/SidebarHeader';
import { QuickAccessSection } from './sidebar/QuickAccessSection';
import { SpacesList } from './sidebar/spaces-list';
import { UserMenu } from './sidebar/UserMenu';
import { SidebarIconButton } from './sidebar/SidebarIconButton';
import { ProjectContextMenu, type ContextMenuAction } from './sidebar/ProjectContextMenu';
import { useAppStore } from '@/store/appStore';
import { useClickOutside } from '@/hooks/useClickOutside';
import type { Space, Category } from '@/types';

interface ContextMenuState {
  isOpen: boolean;
  x: number;
  y: number;
  type: 'space' | 'folder' | 'list' | 'item';
  spaceName: string;
  folderName?: string;
  listName?: string;
  itemId?: string;
  itemType?: string;
  itemName?: string;
}

export function Sidebar({
  isMobile: isMobileProp,
  onClose: onCloseProp,
  onCreateOrg,
  onOrgSettings,
}: { isMobile?: boolean; onClose?: () => void; onCreateOrg?: () => void; onOrgSettings?: () => void } = {}) {
  const {
    spaces,
    categories,
    filters,
    viewMode,
    isMobile: isMobileFromStore,
    totalTasks,
    spaceItems,
    folderMappings,
    stats,
    loading,
    onFilterChange,
    onViewModeChange,
    onOpenCommandPalette,
    onOpenUserSettings,
    onLogout,
    onContextMenuAction,
    onItemClick,
    onClose: onCloseFromStore,
  } = useSidebar();

  const isMobile = isMobileProp ?? isMobileFromStore;
  const onClose = onCloseProp ?? onCloseFromStore;

  const { user } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  
  const setEditingItem = useAppStore((state) => state.setEditingItem);
  const setPendingItemCreation = useAppStore((state) => state.setPendingItemCreation);
  const setSpaces = useAppStore((state) => state.setSpaces);
  const setCategories = useAppStore((state) => state.setCategories);
  const currentSpaces = useAppStore((state) => state.spaces);
  const currentCategories = useAppStore((state) => state.categories);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    isOpen: false,
    x: 0,
    y: 0,
    type: 'space',
    spaceName: '',
  });

  // Handler for creating a new space inline
  const handleCreateSpace = (categoryId?: string) => {
    const tempSpaceId = `temp-${Date.now()}`;
    const tempSpace: Space = {
      id: tempSpaceId,
      name: '',
      taskCount: 0,
      folders: [],
      description: undefined,
    };

    if (categoryId) {
      // Add temp space to the target category
      const updatedCategories = currentCategories.map(c => {
        if (c.id !== categoryId) return c;
        return { ...c, projects: [...c.projects, tempSpace] };
      });
      setCategories(updatedCategories);
    } else {
      // Add temp space to root (uncategorized)
      setSpaces([...currentSpaces, tempSpace]);
    }

    // Set in editing mode
    setEditingItem(tempSpaceId, true);

    // Save creation info
    setPendingItemCreation(tempSpaceId, {
      spaceName: '',
      itemType: 'space',
      containerId: 'root',
      folderId: null,
      categoryId: categoryId || null,
    });
  };

  // Handler for creating a new category inline
  const handleCreateCategory = () => {
    const tempCategoryId = `temp-${Date.now()}`;
    const tempCategory: Category = {
      id: tempCategoryId,
      name: '',
      projects: [],
      taskCount: 0,
    };

    // Add temp category to categories
    setCategories([...currentCategories, tempCategory]);

    // Set in editing mode
    setEditingItem(tempCategoryId, true);

    // Save creation info
    setPendingItemCreation(tempCategoryId, {
      spaceName: '',
      itemType: 'category',
      containerId: 'root',
      folderId: null,
    });
  };

  // Ensure mobile view always uses expanded sidebar
  useEffect(() => {
    if (isMobile) {
      setIsCollapsed(false);
    }
  }, [isMobile]);

  const closeContextMenu = useCallback(() => {
    setContextMenu((prev) => ({ ...prev, isOpen: false }));
  }, []);

  useClickOutside([contextMenuRef], closeContextMenu, contextMenu.isOpen);

  // Close context menu on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeContextMenu();
      }
    };
    if (contextMenu.isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [contextMenu.isOpen, closeContextMenu]);

  const handleContextAction = (actionType: ContextMenuAction['type']) => {
    if (onContextMenuAction) {
      onContextMenuAction({
        type: actionType,
        spaceName: contextMenu.spaceName,
        folderName: contextMenu.folderName,
        listName: contextMenu.listName,
        itemId: contextMenu.itemId,
        itemType: contextMenu.itemType,
      });
    }
    setContextMenu((prev) => ({ ...prev, isOpen: false }));
  };

  const openContextMenu = (
    e: React.MouseEvent,
    type: 'space' | 'folder' | 'list' | 'item',
    spaceName: string,
    folderName?: string,
    listName?: string,
    itemId?: string,
    itemType?: string,
    itemName?: string,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    // Calculate position, ensuring menu stays within viewport
    const x = Math.min(e.clientX, window.innerWidth - 200);
    const y = Math.min(e.clientY, window.innerHeight - 300);

    setContextMenu({
      isOpen: true,
      x,
      y,
      type,
      spaceName,
      folderName,
      listName,
      itemId,
      itemType,
      itemName,
    });
  };

  const handleViewModeChange = (mode: typeof viewMode) => {
    onViewModeChange(mode);
    if (isMobile) {
      onClose?.();
    }
  };

  // Collapsed sidebar view
  if (isCollapsed) {
    return (
      <aside className="w-14 h-screen bg-surface-secondary border-r border-border-primary flex flex-col transition-all duration-200">
        <SidebarHeader
          isCollapsed={true}
          onToggleCollapse={() => setIsCollapsed(false)}
          onClose={onClose}
          onCreateOrg={onCreateOrg}
          onOrgSettings={onOrgSettings}
        />

        {/* Search & Create */}
        <div className="flex flex-col items-center py-2 gap-1 border-b border-border-primary">
          <SidebarIconButton
            icon={<Search className="w-4 h-4" />}
            isActive={false}
            onClick={() => {
              setIsCollapsed(false);
              onOpenCommandPalette?.();
            }}
            tooltip="Cerca (⌘K)"
          />
        </div>

        <div className="flex-1 flex flex-col items-center py-2 gap-1">
          <SidebarIconButton
            icon={<Inbox className="w-4 h-4" />}
            isActive={viewMode === 'inbox'}
            onClick={() => {
              setIsCollapsed(false);
              handleViewModeChange('inbox');
            }}
            tooltip="Inbox"
          />
          <SidebarIconButton
            icon={<Users className="w-4 h-4" />}
            isActive={viewMode === 'team'}
            onClick={() => {
              setIsCollapsed(false);
              handleViewModeChange('team');
            }}
            tooltip="Team"
          />
          <SidebarIconButton
            icon={<Clock className="w-4 h-4" />}
            isActive={viewMode === 'timesheet'}
            onClick={() => {
              setIsCollapsed(false);
              handleViewModeChange('timesheet');
            }}
            tooltip="Timesheet"
          />
        </div>

        {/* User & Expand */}
        <div className="flex flex-col items-center py-2 gap-1 border-t border-border-primary relative">
          <button
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="w-8 h-8 rounded-full bg-linear-to-br from-accent-emerald to-accent-blue flex items-center justify-center hover:ring-2 hover:ring-accent-violet/30 transition-all overflow-hidden"
            title={user?.user_metadata?.full_name || 'Utente'}
          >
            {user?.user_metadata?.avatar_url ? (
              <img src={user.user_metadata.avatar_url} alt="" className="w-8 h-8 rounded-full" />
            ) : (
              <span className="text-[9px] font-bold text-white">
                {(user?.user_metadata?.full_name || 'U').split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
              </span>
            )}
          </button>

          <button
            onClick={() => setIsCollapsed(false)}
            className="p-2 hover:bg-surface-hover transition-colors text-text-quaternary hover:text-text-tertiary"
            title="Espandi sidebar"
          >
            <PanelLeft className="w-4 h-4" />
          </button>
        </div>
      </aside>
    );
  }

  // Expanded sidebar view
  return (
    <aside
      className={clsx(
        'h-screen bg-surface-secondary flex flex-col transition-all duration-200 animate-fade-in',
        isMobile ? 'w-full border-r border-border-primary' : 'w-64 border-r border-border-primary'
      )}
    >
      <SidebarHeader
        isCollapsed={false}
        onToggleCollapse={() => setIsCollapsed(true)}
        onClose={onClose}
        onCreateOrg={onCreateOrg}
        onOrgSettings={onOrgSettings}
      />

      <QuickAccessSection
        viewMode={viewMode}
        stats={stats}
        isLoading={loading}
        onViewModeChange={handleViewModeChange}
      />


      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-3 pb-6">
        <SpacesList
          spaces={spaces}
          categories={categories}
          filters={filters}
          viewMode={viewMode}
          totalTasks={totalTasks}
          loading={loading}
          spaceItems={spaceItems}
          folderMappings={folderMappings}
          onOpenCreateSpace={handleCreateSpace}
          onOpenCreateCategory={handleCreateCategory}
          onFilterChange={onFilterChange}
          onViewModeChange={onViewModeChange}
          onOpenContextMenu={openContextMenu}
          onItemClick={onItemClick}
          isMobile={isMobile}
          onClose={onClose}
        />

        {/* Context Menu Portal */}
        {contextMenu.isOpen && (
          <ProjectContextMenu
            ref={contextMenuRef}
            x={contextMenu.x}
            y={contextMenu.y}
            type={contextMenu.type}
            spaceName={contextMenu.spaceName}
            folderName={contextMenu.folderName}
            listName={contextMenu.listName}
            itemId={contextMenu.itemId}
            itemType={contextMenu.itemType}
            itemName={contextMenu.itemName}
            onAction={handleContextAction}
            onClose={() => setContextMenu((prev) => ({ ...prev, isOpen: false }))}
          />
        )}
      </nav>

      <UserMenu onOpenUserSettings={onOpenUserSettings} onLogout={onLogout} />
    </aside>
  );
}
